import { get, post, ApiServiceError } from '@classic/Foundation/Services/ApiService'
import { WithdrawUrl } from '@mobi/account/Utils'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { UseQueryResult, useQuery } from '@tanstack/react-query'

export interface BankAccountResponse {
  bankName: string
  branchName: string
  bsb: string
  accountNumber: string
  accountName: string
  accountHolderSurname: string
  isPendingWithdrawals: boolean
}

export const useBankDetailsQuery = (): UseQueryResult<BankAccountResponse> => {
  return useQuery({
    queryKey: [queryKeys.getBankAccount],
    queryFn: () => get<BankAccountResponse>({ url: '/api/account/bankaccount' }),
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchIntervalInBackground: false,
    gcTime: 0,
    staleTime: 2 * 1000,
  })
}

export const isBankAccountRegistered = (
  data: BankAccountResponse | undefined
): data is BankAccountResponse => {
  return data !== undefined && data?.bankName?.trim().length > 0
}

export const useBankDetailsFromBsbQuery = (
  bsb: string
): UseQueryResult<BankDetailsFromBsbResponse> => {
  return useQuery<BankDetailsFromBsbResponse>({
    queryKey: [`getBankAccountFromBsb-${bsb}`],
    queryFn: () => get<BankDetailsFromBsbResponse>({ url: WithdrawUrl.getBsbDetails(bsb) }),
    enabled: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchIntervalInBackground: false,
    gcTime: 0,
    staleTime: 2 * 1000,
    retry: (failureCount, error) =>
      failureCount < 3 && (error as ApiServiceError)?.response?.status !== 404,
  })
}

export const isBankDetailsFromBsbFound = (
  data: BankDetailsFromBsbResponse | undefined
): data is BankDetailsFromBsbResponse => {
  return data !== undefined && data.bankName.trim().length > 0
}

interface UpdateBankAccountRequest {
  bankName: string
  branchName: string
  bsb: string
  accountNumber: string
  accountName: string
}

interface UpdateBankAccountResponse {
  isSuccessful: boolean
}

export const postUpdateBankAccount = async (
  bankAccount: UpdateBankAccountRequest
): Promise<UpdateBankAccountResponse> => {
  return await post<UpdateBankAccountResponse>({
    url: '/api/account/updatebankaccount',
    body: bankAccount,
  })
}

export interface BankDetailsFromBsbResponse {
  bsb: string
  bankName: string
  branchName: string
}
